import React from 'react';
import { Link } from 'react-router-dom';
import { StaticPageLayout } from '../StaticPageLayout';
import styles from '../Education/ScienceOfTime.module.scss'; // Reuse science styles for consistency
import { tokens } from '../../../styles/tokens';

export const BlogPostAbstractTime: React.FC = () => {
  return (
    <StaticPageLayout>
      <div className={styles.container}>
        <div style={{ marginBottom: '24px' }}>
          <Link to="/blog" style={{ color: tokens.skyBlue, textDecoration: 'none', fontWeight: 600 }}>
            ← Back to Blog
          </Link>
        </div>

        <div className={styles.header}>
          <h1>Why '5 Minutes' Doesn't Work</h1>
          <p className={styles.subtitle}>
            Most kids ages 3–7 don’t truly understand minutes. Here's how to create urgency using event-based language.
          </p>
        </div>

        <section className={styles.section}>
          <p>
            You’re running <strong>adult time</strong> on a <strong>child brain</strong>.
          </p>
          <p>
            And those two are not the same thing.
          </p>
        </section>

        <section className={styles.section}>
          <h2>Children Don’t Experience Time the Way Adults Do</h2>
          <p>
            For adults, five minutes is concrete. You can feel it. You know what fits inside it.
          </p>
          <p>
            For most children under about 7 years old, it’s just a word.
          </p>
          
          <div className={styles.quoteBox}>
            <p>“Children use time words like minute and hour early in development, but take years to acquire their precise meanings.”</p>
            <span className={styles.source}>— ScienceDirect</span>
          </div>

          <p>
            Developmental research is very clear on this: children begin using time words early ("minute," "later," "tomorrow"), but they can take years to understand what those words actually mean in practice. One large review of child development studies found that young children use time language long before they understand duration or clock-based time in any reliable way.
          </p>
          
          <div className={styles.statCard} style={{ margin: '32px 0' }}>
            <p style={{ fontStyle: 'italic', color: tokens.charcoal }}>
              In other words: when you say "five minutes", your child often hears "not now, but also not clear."
            </p>
          </div>

          <p>That’s why:</p>
          <ul className={styles.tipsList}>
            <li>They keep playing</li>
            <li>They ignore you</li>
            <li>They melt down when time is "suddenly" up</li>
            <li>They seem shocked when you actually try to leave</li>
          </ul>
          <p>From their perspective, the ending came out of nowhere.</p>
        </section>

        <section className={styles.section}>
          <h2>Why Verbal Warnings Fail (Even When You Repeat Them)</h2>
          <p>Parents are often told to give warnings:</p>
          <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '24px 0', textAlign: 'center', fontWeight: 600, color: tokens.charcoal }}>
            <li>"Five minutes."</li>
            <li>"Two more minutes."</li>
            <li>"Last minute!"</li>
          </ul>
          <p>
            The intention is good. The execution assumes a skill your child may not have yet.
          </p>

          <div className={styles.quoteBox}>
            <p>“Until a child is able to discern the difference between one minute and ten minutes… a five-minute warning may cause more confusion than provide clarity.”</p>
            <span className={styles.source}>— KinderCare</span>
          </div>

          <p>
            Research on early childhood cognition shows that before roughly 6–8 years old, children rely much more on:
          </p>
          <ul className={styles.tipsList}>
            <li><strong>Events</strong> ("after this episode")</li>
            <li><strong>Sequences</strong> ("first shoes, then car")</li>
            <li><strong>Visible change</strong></li>
          </ul>
          <p>
            They do not reliably rely on abstract measurement. That’s why even well-meaning countdowns can backfire: they don't build urgency, they don't support planning, and they often increase conflict.
          </p>
          <p>
            To a child, "two minutes" and "ten minutes" can feel identical.
          </p>
        </section>

        <section className={styles.section}>
          <h2>What Children Actually Respond To: Events, Not Numbers</h2>
          <p>
            What does work is shifting from time-based language to <strong>event-based signals</strong>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', margin: '32px 0' }}>
            <div style={{ padding: '24px', background: '#FFF5F5', borderRadius: '16px' }}>
              <h4 style={{ color: tokens.softRed, marginBottom: '16px' }}>Instead of:</h4>
              <p>"We're leaving in five minutes"</p>
            </div>
            <div style={{ padding: '24px', background: '#F0FFF4', borderRadius: '16px' }}>
              <h4 style={{ color: '#38A169', marginBottom: '16px' }}>Try:</h4>
              <p>"When this red part disappears, we leave"</p>
              <p>"When the song ends, it's shoes time"</p>
              <p>"After this page, we go to the car"</p>
            </div>
          </div>
          <p>
            This aligns with decades of developmental research showing that young children understand before/after far earlier than minutes/hours. They don’t need to calculate time. <strong>They need to see it move.</strong>
          </p>
        </section>

        <section className={styles.section}>
          <h2>Making Time Visible Changes Everything</h2>
          <p>
            This is where visual time matters. Studies on self-regulation in young children show that when time is represented visually—for example as a shrinking shape rather than a ticking number—children transition more smoothly and show better emotional regulation.
          </p>
          <p>
            Why? Because the child isn’t relying on memory or abstraction. <strong>They’re watching time happen.</strong>
          </p>
        </section>

        <section className={styles.section}>
          <h2>Why Wedges Work (and Numbers Don’t)</h2>
          <p>A wedge-based visual clock does something powerful:</p>
          <ul className={styles.tipsList}>
            <li>It turns time into <strong>space</strong></li>
            <li>It shows <strong>progress</strong>, not just an endpoint</li>
            <li>It removes the "sudden stop" problem</li>
          </ul>
          <p>
            Instead of an alarm that feels like punishment, the child sees: <em>"Oh… it’s getting smaller."</em>
          </p>
          <p>
            This supports something called <strong>anticipatory regulation</strong>—the ability to emotionally prepare for what’s coming next. That skill doesn’t emerge automatically; it has to be supported externally first.
          </p>
        </section>

        <section className={styles.section}>
          <h2>What to Say Instead (Simple Scripts That Work)</h2>
          <p>Here are examples parents consistently find more effective than verbal countdowns:</p>
          
          <div style={{ background: 'white', padding: '32px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
                <span>💬</span> <strong>"When the wedge is gone, we go."</strong>
              </li>
              <li style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
                <span>💬</span> <strong>"You can keep playing until the red is finished."</strong>
              </li>
              <li style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
                <span>💬</span> <strong>"Let’s check how much time is left together."</strong>
              </li>
              <li style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
                <span>💬</span> <strong>"You’re the helper when the color disappears."</strong>
              </li>
              <li style={{ display: 'flex', gap: '12px' }}>
                <span>💬</span> <strong>"When this finishes, we do shoes → coat → car."</strong>
              </li>
            </ul>
          </div>
          
          <p style={{ marginTop: '24px' }}>Notice what’s missing: No numbers. No bargaining. No repeated warnings. Just one clear, visible signal.</p>
        </section>

        <section className={styles.section}>
          <h2>The Takeaway</h2>
          <p>
            If leaving the house takes forever, it’s rarely because your child is being difficult. More often, it’s because time is invisible, the ending feels abrupt, and expectations are abstract.
          </p>
          <p>
            When you make time visible, predictable, and shared, children don’t just comply faster—they feel safer doing so.
          </p>
          <p>
            That’s the problem we’re building our app to solve. Not by rushing children. Not by nagging parents. But by turning time into something kids can actually understand while leaving parents in control.
          </p>
        </section>

        <div className={styles.cta}>
          <h3>Ready to stop the struggle?</h3>
          <p style={{ color: tokens.paperCream, marginBottom: '24px' }}>MyMinu gives you the visual tools to make "time" real for your child.</p>
          <Link to="/register" className={styles.primaryButton}>
            Start Your Routine Free
          </Link>
        </div>

        <div className={styles.sourcesSection}>
          <h3>Sources</h3>
          
          <div className={styles.sourceItem}>
            <p>Tillman, K.A. & Barner, D. (2015). Learning the language of time: Children’s acquisition of duration words. Cognition.</p>
            <a href="https://www.sciencedirect.com/science/article/pii/S0010028515000304" target="_blank" rel="noopener noreferrer">
              https://www.sciencedirect.com/science/article/pii/S0010028515000304
            </a>
          </div>

          <div className={styles.sourceItem}>
            <p>KinderCare: “Time to Go! … Five-Minute Rule … Visual Timers” (2018)</p>
            <a href="https://www.kindercare.com/content-hub/articles/2018/january/visual-timers" target="_blank" rel="noopener noreferrer">
              https://www.kindercare.com/content-hub/articles/2018/january/visual-timers
            </a>
          </div>

          <div className={styles.sourceItem}>
            <p>NAEYC (Young Children, 2018): Reducing Challenging Behaviors during Transitions…</p>
            <a href="https://www.naeyc.org/resources/pubs/yc/sep2018/reducing-challenging-behaviors-during-transitions" target="_blank" rel="noopener noreferrer">
              https://www.naeyc.org/resources/pubs/yc/sep2018/reducing-challenging-behaviors-during-transitions
            </a>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
};
